/**
 * TypeChip — Tier 0 module-local ISA / MSA discriminator pill.
 *
 * Per `.design-incoming/admin-20260522/COMPONENT_API.md §TypeChip` +
 * `DATA_MODEL.md §1`. Two values: `"ISA"` (single-org system) and
 * `"MSA"` (multi-system aggregator). Visually distinct color
 * families so admins can scan a long Companies list and tell the
 * two kinds apart at a glance.
 *
 * Color choice (aligned with `source/admin.jsx` line 58–67 — the
 * inspectable bundle source is the canonical authority on visual
 * tone here):
 *   - MSA → brand-tinted (`--brand-500` 14% mix + `--brand-700` fg)
 *   - ISA → neutral (`--bg-sunken` + `--fg-secondary`)
 *
 * Rationale per the bundle: MSAs are the rarer / aggregator shape,
 * so the brand-tint draws the eye when scanning a Companies list
 * that's predominantly ISAs. The earlier prose on this branch had
 * the mapping flipped; Codex caught the drift between prose and
 * source — bundle source is the canonical truth.
 *
 * The chip is a plain styled `<span>` rather than composing the
 * kit `Badge` (Badge is in PR #143, pending review) — the two
 * type-values are stable, narrow, and don't need the kit Badge's
 * variant matrix. If a future ticket adds more company types, the
 * chip should promote to compose kit `Badge` then.
 */

import * as React from 'react';

export type CompanyType = 'ISA' | 'MSA';

export interface TypeChipProps {
  readonly type: CompanyType;
  readonly size?: 'sm' | 'md';
}

interface ChipVisual {
  readonly background: string;
  readonly foreground: string;
  readonly border: string;
}

// Source of truth: `.design-incoming/admin-20260522/source/admin.jsx`
// line 58–67. The exact brand-mix percentages (14% bg, 30% border)
// are ported verbatim from the source so this primitive stays
// visually identical to the bundle's reference render.
const VISUALS: Record<CompanyType, ChipVisual> = {
  ISA: {
    background: 'var(--bg-sunken)',
    foreground: 'var(--fg-secondary)',
    border: 'var(--border-subtle)',
  },
  MSA: {
    background:
      'color-mix(in oklab, var(--brand-500) 14%, var(--bg-surface))',
    foreground: 'var(--brand-700)',
    border: 'color-mix(in oklab, var(--brand-500) 30%, transparent)',
  },
};

export function TypeChip({
  type,
  size = 'md',
}: TypeChipProps): React.ReactElement {
  const visual = VISUALS[type];
  const fontSize = size === 'sm' ? '10px' : '11px';
  const padY = size === 'sm' ? '1px' : '2px';
  const padX = size === 'sm' ? '6px' : '8px';

  return (
    <span
      aria-label={`Company type: ${type}`}
      className="inline-flex items-center font-bold uppercase"
      style={{
        padding: `${padY} ${padX}`,
        fontSize,
        letterSpacing: '0.6px',
        borderRadius: 'var(--radius-full)',
        background: visual.background,
        color: visual.foreground,
        border: `1px solid ${visual.border}`,
      }}
    >
      {type}
    </span>
  );
}
